import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Pagination from './pagination';
import './tab.css';

const apiUrl = process.env.REACT_APP_API_URL;

// Optimized: Fetch single page instead of all pages
const fetchArticlesByCategory = async ({ categoryId, page = 1, perPage = 15 }) => {
  const response = await fetch(`${apiUrl}/api/news?category_id=${categoryId}&page=${page}&per_page=${perPage}`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch articles for category ${categoryId}`);
  }
  
  return response.json();
};

const fetchCategories = async () => {
  const response = await fetch(`${apiUrl}/api/categories`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch categories');
  }
  
  return response.json();
};

// API function to delete article - with debugging
const deleteArticle = async (articleId) => {
  

  console.trace('🔥 API: Delete call stack trace:');
  
  const response = await fetch(`${apiUrl}/api/news/${articleId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('authToken')}`
    },
  });
  
  if (!response.ok) {
    throw new Error('Failed to delete article');
  }
  
  const result = await response.json();

  return result;
};

// Delete Confirmation Modal Component
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, articleTitle, isDeleting }) => {
  if (!isOpen) return null;

  return (
    <div className="delete-modal-overlay">
      <div className="delete-modal-content">
        <div className="delete-modal-icon">
          ⚠️
        </div>
        
        <h3 className="delete-modal-title">
          تأكيد الحذف
        </h3>
        
        <p className="delete-modal-subtitle">
          هل أنت متأكد من أنك تريد حذف هذا المقال؟
        </p>
        
        <div className="delete-modal-article-title">
          <strong>
            "{articleTitle?.length > 60 ? `${articleTitle.substring(0, 60)}...` : articleTitle}"
          </strong>
        </div>
        
        <p className="delete-modal-warning">
          لا يمكن التراجع عن هذا الإجراء
        </p>
        
        <div className="delete-modal-actions">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="delete-modal-cancel"
          >
            إلغاء
          </button>
          
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="delete-modal-confirm"
          >
            {isDeleting ? (
              <>
                <div className="delete-spinner"></div>
                جاري الحذف...
              </>
            ) : (
              'حذف المقال'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const ArticlesTable = ({ onEditArticle, onArticleUpdated }) => {
  const [currentPages, setCurrentPages] = React.useState({ 1: 1, 2: 1, 3: 1 });
  const [deleteModal, setDeleteModal] = React.useState({ isOpen: false, articleId: null, articleTitle: '', categoryId: null });
  const perPageSettings = { 1: 10, 2: 10, 3: 10 }; 
  const queryClient = useQueryClient();

  // Optimized queries - fetch single page per category
  const category1Query = useQuery({
    queryKey: ['articles', 'category', 1, currentPages[1]],
    queryFn: () => fetchArticlesByCategory({ 
      categoryId: 1, 
      page: currentPages[1], 
      perPage: perPageSettings[1] 
    }),
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const category2Query = useQuery({
    queryKey: ['articles', 'category', 2, currentPages[2]],
    queryFn: () => fetchArticlesByCategory({ 
      categoryId: 2, 
      page: currentPages[2], 
      perPage: perPageSettings[2] 
    }),
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const category3Query = useQuery({
    queryKey: ['articles', 'category', 3, currentPages[3]],
    queryFn: () => fetchArticlesByCategory({ 
      categoryId: 3, 
      page: currentPages[3], 
      perPage: perPageSettings[3] 
    }),
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    isError: categoriesError
  } = useQuery({
    queryKey: ['categories'],
    queryFn: fetchCategories,
    staleTime: 10 * 60 * 1000, 
  });

  // Delete mutation with onSuccess restored
  const deleteMutation = useMutation({
    mutationFn: deleteArticle,
    onMutate: async (articleId) => {
    
    },
    onSuccess: (data, articleId) => {
     
      
      // Only invalidate the specific category that contained the deleted article
      if (deleteModal.categoryId) {
    
        queryClient.invalidateQueries(['articles', 'category', deleteModal.categoryId]);
      } else {
        
        queryClient.invalidateQueries(['articles', 'category']);
      }
      
      setDeleteModal({ isOpen: false, articleId: null, articleTitle: '', categoryId: null });
    },
    onError: (error, articleId) => {
      console.error('ArticlesTable: Delete failed for article', articleId, ':', error.message);
      alert(`خطأ في حذف المقالة: ${error.message}`);
      setDeleteModal({ isOpen: false, articleId: null, articleTitle: '', categoryId: null });
    },
  });

  // Targeted refresh function
  const refreshArticleData = React.useCallback((specificCategoryId = null) => {
    
    
    if (specificCategoryId) {
     
      // Only refresh specific category with current page
      queryClient.invalidateQueries(['articles', 'category', specificCategoryId]);
    } else {
      
      // Refresh all categories (fallback)
      queryClient.invalidateQueries(['articles', 'category', 1]);
      queryClient.invalidateQueries(['articles', 'category', 2]);
      queryClient.invalidateQueries(['articles', 'category', 3]);
    }
  }, [queryClient]);

  // Call this function when parent component updates an article
  React.useEffect(() => {
    if (onArticleUpdated) {
      window.refreshArticleData = refreshArticleData;
    }
  }, [onArticleUpdated, refreshArticleData]);

  const handlePageChange = (categoryId, newPage) => {
    setCurrentPages(prev => ({
      ...prev,
      [categoryId]: newPage
    }));
  };

  const handleDeleteClick = (articleId, articleTitle, categoryId) => {
    // Prevent multiple clicks while deleting
    if (deleteMutation.isLoading) return;
    
   
    
    setDeleteModal({
      isOpen: true,
      articleId,
      articleTitle,
      categoryId // Store category ID to target invalidation
    });
  };

  const handleDeleteConfirm = () => {
    // Prevent double submission
    if (deleteModal.articleId && !deleteMutation.isLoading) {
      
      deleteMutation.mutate(deleteModal.articleId);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, articleId: null, articleTitle: '', categoryId: null });
  };

  const handleEdit = (article) => {
    if (onEditArticle) {
     
      const targetedRefresh = () => {
       
        refreshArticleData(article.category_id);
      };
      onEditArticle(article, targetedRefresh);
    }
  };

  // Hardcoded category names
  const getCategoryDisplayName = (categoryId) => {
    switch(categoryId) {
      case 1: return 'وسط';
      case 2: return 'مقالات';
      case 3: return 'أخبار رياضية';
      default: return 'غير محدد';
    }
  };

  // Get data for specific category
  const getCategoryData = (categoryId) => {
    let queryData, isLoading, isError, error, isFetching;
    
    switch(categoryId) {
      case 1:
        queryData = category1Query.data;
        isLoading = category1Query.isLoading;
        isError = category1Query.isError;
        error = category1Query.error;
        isFetching = category1Query.isFetching;
        break;
      case 2:
        queryData = category2Query.data;
        isLoading = category2Query.isLoading;
        isError = category2Query.isError;
        error = category2Query.error;
        isFetching = category2Query.isFetching;
        break;
      case 3:
        queryData = category3Query.data;
        isLoading = category3Query.isLoading;
        isError = category3Query.isError;
        error = category3Query.error;
        isFetching = category3Query.isFetching;
        break;
      default:
        return { queryData: null, isLoading: false, isError: false, error: null, isFetching: false };
    }
    
    return { queryData, isLoading, isError, error, isFetching };
  };

  // Get articles by category with proper pagination data
  const getArticlesByCategory = (categoryId) => {
    const { queryData } = getCategoryData(categoryId);
    
    if (!queryData?.success || !queryData?.data) {
      return { articles: [], totalPages: 0, totalCount: 0 };
    }
    
    return { 
      articles: queryData.data, 
      totalPages: queryData.pagination?.last_page || 0,
      totalCount: queryData.pagination?.total || 0
    };
  };

  const renderTable = (categoryId) => {
    const { articles, totalPages, totalCount } = getArticlesByCategory(categoryId);
    const { isLoading, isError, error, isFetching } = getCategoryData(categoryId);
    const categoryName = getCategoryDisplayName(categoryId);
    const currentPage = currentPages[categoryId];

    if (isLoading) {
      return (
        <div key={categoryId}>
          <div className="category-header">
            <h3 className="category-title">
              {categoryName}
            </h3>
          </div>
          
          <div className="card mb-5">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              
              <h4 className="loading-title">
                جاري تحميل مقالات {categoryName}...
              </h4>
              
              <p className="loading-subtitle">
                يرجى الانتظار
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (isError) {
      return (
        <div key={categoryId}>
          <div className="category-header">
            <h3 className="category-title">
              {categoryName}
            </h3>
          </div>
          
          <div className="card mb-5">
            <div className="alert alert-danger">
              <p>{error?.message || 'حدث خطأ في تحميل المقالات'}</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={categoryId}>
        <div className="category-header">
          <h3 className="category-title">
            {categoryName}
          </h3>
          <p className="category-count">
            ({totalCount || 0} مقالة إجمالي، {articles.length} معروض)
          </p>
        </div>
        
        <div className="card mb-5">
          {(isFetching || deleteMutation.isLoading) && (
            <div className="updating-indicator">
              {deleteMutation.isLoading ? (
                <div className="deleting-indicator">
                  <div className="delete-spinner"></div>
                  جاري الحذف...
                </div>
              ) : (
                <small className="text-muted">جاري التحديث...</small>
              )}
            </div>
          )}
          
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>العنوان</th>
                  <th>الأولوية</th>      
                  <th>مضاف إلى شريط</th> 
                  <th>التاريخ</th>
                  <th>الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {articles.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4"> 
                      لا توجد مقالات في هذا القسم حالياً
                    </td>
                  </tr>
                ) : (
                  articles.map((article) => {
                    return (
                      <tr key={article.id}>
                        <td>
                          <div className="article-title" title={article.title}>
                            {article.title?.length > 50 
                              ? `${article.title.substring(0, 50).trim()}...` 
                              : article.title || 'بدون عنوان'
                            }
                          </div>
                        </td>
                        <td>
                          {article.priority != null ? article.priority : '-'}  
                        </td>
                        <td>
                          {article.add_to_tinker === 1 ? 'نعم' : 'لا'}
                        </td>
                        <td>
                          {article.published_at 
                            ? new Date(article.published_at).toLocaleDateString('ar-EG', { year: 'numeric', month: '2-digit', day: '2-digit' })
                            : article.created_at 
                            ? new Date(article.created_at).toLocaleDateString('ar-EG', { year: 'numeric', month: '2-digit', day: '2-digit' })
                            : '-'}
                        </td>
                        <td>
                          <div className="action-links">
                            <a href="#" className="action-link edit" onClick={(e) => { 
                              e.preventDefault(); 
                              handleEdit(article);
                            }}>
                              تعديل
                            </a>
                            <a 
                              href="#" 
                              className={`action-link delete ${deleteMutation.isLoading ? 'disabled' : ''}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation(); // Prevent event bubbling
                                if (!deleteMutation.isLoading && !deleteModal.isOpen) {
                                  handleDeleteClick(article.id, article.title, categoryId);
                                }
                              }}
                            >
                              حذف
                            </a>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          
          {totalPages > 1 && (
            <Pagination 
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(newPage) => handlePageChange(categoryId, newPage)}
            />
          )}
        </div>
      </div>
    );
  };

  if (categoriesLoading) {
    return (
      <div className="articles-container">
        <div className="main-header">
          <h2 className="main-title">
            إدارة المقالات حسب الأقسام
          </h2>
          <p className="main-subtitle">
            عرض وإدارة جميع المقالات مقسمة حسب الفئات
          </p>
        </div>

        <div className="main-loading-container">
          <div className="main-loading-spinner"></div>
          
          <h3 className="main-loading-title">
            جاري تحميل الفئات...
          </h3>
          
          <p className="main-loading-subtitle">
            يرجى الانتظار بينما نقوم بتحضير بيانات المقالات
          </p>
        </div>
      </div>
    );
  }

  if (categoriesError) {
    return (
      <div className="card">
        <div className="table-container">
          <div className="alert alert-danger">
            <h4>خطأ في تحميل البيانات</h4>
            <p>{categoriesError?.message || 'حدث خطأ غير متوقع'}</p>
            <button 
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              إعادة المحاولة
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="articles-container">
      <div className="main-header">
        <h2 className="main-title">
          إدارة المقالات حسب الأقسام
        </h2>
        <p className="main-subtitle">
          عرض وإدارة جميع المقالات مقسمة حسب الفئات
        </p>
      </div>
      
      {[1, 2, 3].map(categoryId => renderTable(categoryId))}
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        articleTitle={deleteModal.articleTitle}
        isDeleting={deleteMutation.isLoading}
      />
    </div>
  );
};

export default ArticlesTable;